import {Injectable} from "@angular/core";
import {NetworkService} from "~/app/core/network.service";
import {SettingsService} from "~/app/core/settings.service";
import {Credentials} from "~/app/core/model/rpc/credentials";
import {WFRequest} from "~/app/core/model/rpc/wf-request";
import {WFResponse} from "~/app/core/model/rpc/wf-response";
import {UserInfo} from "~/app/core/model/user-info";
import {Settings} from "~/app/core/model/settings";
import {StorageService} from "~/app/core/storage.service";
import {ExternalAuthCredentials} from "~/app/core/model/rpc/external-auth-credentials";
import {NewAccountCredentials} from "~/app/core/model/rpc/new-account-credentials";
import {VerificationCredentials} from "~/app/core/model/rpc/verification-credentials";
import {InitDeviceToken} from "~/app/core/model/rpc/init-device-token";
import {ResetPasswordCredentials} from "~/app/core/model/rpc/reset-password-credentials";
import {OfferCriteria} from "~/app/core/model/rpc/offer-criteria";
import {OfferAvailableInfo} from "~/app/core/model/rpc/offer-available-info";
import {BooleanValue} from "~/app/core/model/rpc/boolean-value";
import {OfferInfoList} from "~/app/core/model/rpc/offer-info-list";
import {toDate, toDateStr} from "~/app/core/model/date-str";
import * as Log from "~/app/core/log";
import {NavigationService} from "~/app/core/navigation.service";
import {UserInfoRPC} from "~/app/core/model/rpc/user-info-rpc";
import {AccessTokenPlus} from "~/app/core/model/rpc/access-token-plus";
import {AlternateLocationsList} from "~/app/core/model/rpc/alternate-locations-list";
import {LocationInfoRPC} from "~/app/core/model/rpc/location-info-rpc";
import {RPCRequest} from "~/app/core/model/rpc/rpc-request";
import {InteractionAggregate} from "~/app/core/model/interaction/interaction-aggregate";
import {InteractionService} from "~/app/core/interaction.service";
import {StringValue} from "~/app/core/model/rpc/string-value";
import {Subject} from "rxjs";
import {DeviceSpecs} from "~/app/core/model/rpc/device-specs";
import {ContactUsData} from "~/app/core/model/rpc/contact-us-data";
import {WebContent} from "~/app/core/model/rpc/web-content";
import {ErrorCode} from "~/app/core/model/rpc/error-code";
import {TicketInfo} from "~/app/core/model/ticket-info";
import {OfferInfo} from "~/app/core/model/rpc/offer-info";
import {HttpClient, HttpHeaders} from "@angular/common/http";

export enum NetworkConnectionType {
    Wifi, Mobile, None
}

@Injectable()
export class RPCService {
    private readonly API_VERSION: number = 5;

    private readonly testUrl: string = "https://api.whutsfree.com/test-rpc";
    private readonly prodUrl: string = "https://api.whutsfree.com/prod-rpc";

    private queueInProgress: Subject<boolean>;
    private asyncQueue: Array<RPCRequest<any>>;

    private get settings(): Settings {
        return this.settingsService.loadSettings();
    }

    private get url(): string {
        const env = this.settings.env;
        if (env === "prod") {
            return this.prodUrl;
        } else if (env === "test") {
            return this.testUrl;
        } else if (env === "local") {
            const localUrl = this.settings.localUrl;
            if (localUrl) {
                return localUrl + "/rpc";
            }
        }

        return this.prodUrl;
    }

    constructor(
        private readonly httpClient: HttpClient,
        private readonly settingsService: SettingsService,
        private readonly storageService: StorageService,
        private readonly networkService: NetworkService,
        private readonly navigationService: NavigationService,
        private readonly interactionService: InteractionService
    ) {
        this.asyncQueue = this.storageService.loadRPCQueue();
        this.interactionService.registerFlushQueue(() => {
            this.processQueueWithInteractions(() => {
                // done
            });
        });
    }

    AlternateLocations(criteria: OfferCriteria): Promise<AlternateLocationsList> { return this.rpcCall("app", "AlternateLocations", criteria); }
    AppleAuth(credentials: ExternalAuthCredentials): Promise<AccessTokenPlus> { return this.rpcCall("app", "AppleAuth", credentials); }
    ContactUs(data: ContactUsData): Promise<void> { return this.rpcCall("app", "ContactUs", data); }
    CreateAccount(credentials: NewAccountCredentials): Promise<AccessTokenPlus> { return this.rpcCall("app", "CreateAccount", credentials); }
    DeleteToken(): Promise<void> { return this.rpcCall("app", "DeleteToken", {}); }
    DeleteUser(): Promise<void> { return this.rpcCall("app", "DeleteUser", {}); }
    FacebookAuth(credentials: ExternalAuthCredentials): Promise<AccessTokenPlus> { return this.rpcCall("app", "FacebookAuth", credentials); }
    FirstUse_queue(): void { this.rpcCall("app", "FirstUse", {}, true); }
    ForgotPassword(credentials: Credentials): Promise<VerificationCredentials> { return this.rpcCall("app", "ForgotPassword", credentials); }
    GetToken(credentials: Credentials): Promise<AccessTokenPlus> { return this.rpcCall("app", "GetToken", credentials); }
    GetZip(): Promise<string> { return this.rpcCall("app", "GetZip", {}); }
    GoogleAuth(credentials: ExternalAuthCredentials): Promise<AccessTokenPlus> { return this.rpcCall("app", "GoogleAuth", credentials); }
    InitDevice(deviceSpecs: DeviceSpecs): Promise<InitDeviceToken> { return this.rpcCall("app", "InitDevice", deviceSpecs); }
    InteractionAggregate_queue(aggregate: InteractionAggregate): void { this.rpcCall("app", "InteractionAggregate", aggregate, true); }
    OfferAdditionalPurchase_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferAdditionalPurchase", criteria, true); }
    OfferNoAdditionalPurchase_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferNoAdditionalPurchase", criteria, true); }
    OfferClaim_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferClaim", criteria, true); }
    OfferGenerateShare(criteria: OfferCriteria): Promise<StringValue> { return this.rpcCall("app", "OfferGenerateShare", criteria); }
    OfferGenerateShareWithImage(criteria: OfferCriteria): Promise<{ url: string, image: string }> { return this.rpcCall("app", "OfferGenerateShareWithImage", criteria); }
    OfferIsAvailable(criteria: OfferCriteria): Promise<OfferAvailableInfo> { return this.rpcCall("app", "OfferIsAvailable", criteria); }
    OfferIsFirstTime(criteria: OfferCriteria): Promise<BooleanValue> { return this.rpcCall("app", "OfferIsFirstTime", criteria); }
    OfferIsRedeemed(criteria: OfferCriteria): Promise<BooleanValue> { return this.rpcCall("app", "OfferIsRedeemed", criteria); }
    OfferNearby_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferNearby", criteria, true); }
    OfferRedeem(criteria: OfferCriteria): Promise<void> { return this.rpcCall("app", "OfferRedeem", criteria); }
    OfferRedeem_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferRedeem", criteria, true); }
    OfferReset(criteria: {offer_id: number, resetRedeemed: boolean, resetRevealed: boolean, resetRepeatUnlocked: boolean, resetThumbsup: boolean, resetBounceback: boolean, resetVideo: boolean}): Promise<OfferInfo> { return this.rpcCall("app", "OfferReset", criteria); }
    OfferSearch(criteria: OfferCriteria): Promise<OfferInfoList> { return this.rpcCall("app", "OfferSearch", criteria); }
    OfferAll(): Promise<OfferInfoList> {return this.rpcCall("app", "OfferAll", {});}
    OfferThumbsSkip_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferThumbsSkip", criteria, true); }
    OfferThumbsDown_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferThumbsDown", criteria, true); }
    OfferThumbsUp_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferThumbsUp", criteria, true); }
    OfferView_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferView", criteria, true); }
    OfferReveal_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferReveal", criteria, true); }
    OfferFavorite_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferFavorite", criteria, true); }
    OfferUnfavorite_queue(criteria: OfferCriteria): void { this.rpcCall("app", "OfferUnfavorite", criteria, true); }
    OfferRepeatUnlock(criteria: OfferCriteria): Promise<void> { return this.rpcCall("app", "OfferRepeatUnlock", criteria); }
    TicketsFetch(): Promise<TicketInfo> { return this.rpcCall("app", "TicketsFetch", {}); }
    TicketsSet(ticketCount: number): Promise<TicketInfo> { return this.rpcCall("app", "TicketsSet", ticketCount); }
    TicketsResetDailyTasks(): Promise<TicketInfo> { return this.rpcCall("app", "TicketsResetDailyTasks", {}); }
    TicketsSpend(ticketCount: number): Promise<TicketInfo> { return this.rpcCall("app", "TicketsSpend", ticketCount); }
    TicketsSpend_queue(ticketCount: number): void { this.rpcCall("app", "TicketsSpend", ticketCount, true); }
    TicketsVerifyReferral(info: { referralCode: string, ticketsToSignUp: number, ticketsToReferrer: number}): Promise<boolean> { return this.rpcCall("app", "TicketsVerifyReferral", info); }
    TicketsActionEarned(info: { action: "openApp"|"openOffer"|"share"|"filter"|"webVisit"|"favorite", tickets: number}): Promise<TicketInfo> { return this.rpcCall("app", "TicketsActionEarned", info); }
    TicketsActionEarned_queue(info: { action: "openApp"|"openOffer"|"share"|"filter"|"webVisit"|"favorite", tickets: number}): void { this.rpcCall("app", "TicketsActionEarned", info, true); }
    TicketsRedeemEarned(info: { offer_id: number, location_id: number, tickets: number}): Promise<TicketInfo> { return this.rpcCall("app", "TicketsRedeemEarned", info); }
    TicketsRedeemEarned_queue(info: { offer_id: number, location_id: number, tickets: number}): void { this.rpcCall("app", "TicketsRedeemEarned", info, true); }
    ResendToken(credentials: Credentials): Promise<void> { return this.rpcCall("app", "ResendToken", credentials); }
    ResetOffers(): Promise<void> { return this.rpcCall("app", "ResetOffers", {}); }
    ResetPassword(credentials: ResetPasswordCredentials): Promise<AccessTokenPlus> { return this.rpcCall("app", "ResetPassword", credentials); }
    UpdateZip(location: LocationInfoRPC): Promise<void> { return this.rpcCall("app", "UpdateZip", location); }
    shareCode(): Promise<string> { return this.rpcCall("app", "GetReferralCode", {}); }
    UserInfo(): Promise<UserInfoRPC> { return this.rpcCall("app", "UserInfo", {}); }
    VerifyAccount(credentials: VerificationCredentials): Promise<AccessTokenPlus> { return this.rpcCall("app", "VerifyAccount", credentials); }
    WebShareHTML(urlKey: string): Promise<WebContent> { return this.rpcCall("app", "WebShareHTML", { value: urlKey }); }

    flushAsync(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.processQueue(() => {
                resolve();
            });
        });
    }

    private rpcCall<I, O>(namespace: "app" | "admin", method: string, input: I, async: boolean = false): Promise<O> {
        const userInfo: UserInfo = this.storageService.loadUserInfo(this.settings.env);
        const request: WFRequest<I> = {
            version: this.API_VERSION,
            auth: userInfo ? userInfo.authToken : null,
            device: this.storageService.loadDevice(),
            namespace: namespace,
            method: method,
            data: input
        };
        // optionally force in override timestamp for API calls
        if (this.settings.dateOverride) {
            request.now = toDateStr(this.settings.calculateCurrentTime());
        } else if (async) {
            // since async requests might be delayed for a while, capture the starting timestamp to send that to the app
            request.now = toDateStr(new Date());
        }

        const rpcReq: RPCRequest<I> = {
            url: this.url,
            request: request,
            timeout: 30000,
            attempts: 0
        };
        if (async) {
            // queue request
            this.enqueue(rpcReq);
            return null;
        } else {
            // if any calls are queued, they need to be processed before invoking the next request so that things happen in proper sequence on server
            return new Promise((resolve, reject) => {
                this.processQueueWithInteractions(() => {
                    this.invokeRpc(rpcReq).then((result: O) => {
                        resolve(result);
                    }, (error) => {
                        reject(error);
                    });
                });
            });
        }
    }

    private invokeRpc<I, O>(request: RPCRequest<I>): Promise<O> {
        Log.debug(Log.Tag.RPCService, "REQ(" + request.url + "): " + JSON.stringify(request));
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type':  'application/json'
            }),
            withCredentials: false
        };
        return new Promise((resolve, reject) => {
            this.httpClient.post(request.url, request.request, httpOptions).subscribe((result: WFResponse<O>) => {
                if (result.minVersion > this.API_VERSION) {
                    // fatal error and app must be upgraded
                    this.navigationService.navPageUpgrade();
                } else if (result.code === ErrorCode.InvalidateSession) {
                    this.navigationService.navPageLogout();
                } else {
                    Log.debug(Log.Tag.RPCService, "RESP: " + JSON.stringify(result));
                    if (result.success) {
                        resolve(result.data);
                    } else {
                        reject(result.code);
                    }
                }
            }, (error) => {
                reject(error);
            });
        });
    }

    private enqueue<I, O>(request: RPCRequest<I>): void {
        this.asyncQueue.push(request);
        this.storageService.storeRPCQueue(this.asyncQueue);
    }

    private processQueueWithInteractions(done): void {
        const aggr: InteractionAggregate = this.storageService.loadInteraction();
        const now = new Date().getTime();
        if (aggr.wifiDeadline == null) {
            aggr.wifiDeadline = toDateStr(new Date());
        }
        if (toDate(aggr.deadline).getTime() < now || (this.networkService.connectionType === NetworkConnectionType.Wifi && toDate(aggr.wifiDeadline).getTime() < now)) {
            Log.trace(Log.Tag.RPCService, "Enqueue interaction data to transmit");

            this.InteractionAggregate_queue(aggr);
            this.storageService.storeInteraction(null);
        }
        this.processQueue(done);
    }

    private processQueue(done, isContinued: boolean = false): void {
        if (!isContinued && this.queueInProgress) {
            // another process is purging the queue, so wait for it instead of having to processes stomping on each other
            this.queueInProgress.subscribe((val) => {
                done();
            });
        } else {
            if (this.asyncQueue.length > 0 && this.networkService.connectionType !== NetworkConnectionType.None) {
                this.queueInProgress = new Subject<boolean>();
                const req: RPCRequest<any> = this.asyncQueue[0];
                this.invokeRpc(req).then(() => {
                    this.asyncQueue.splice(0, 1);
                    this.storageService.storeRPCQueue(this.asyncQueue);
                    this.processQueue(done, true);
                }, (error) => {
                    const attempts = req.attempts ? req.attempts : 0;
                    req.attempts = attempts + 1;
                    if (attempts < 3) {
                        // just store with the updated attempt count and try again
                        this.storageService.storeRPCQueue(this.asyncQueue);
                        this.processQueue(done, true);
                    } else {
                        this.asyncQueue.splice(0, 1);
                        this.storageService.storeRPCQueue(this.asyncQueue);
                        this.processQueue(done, true);
                    }
                });
            } else {
                if (this.queueInProgress) {
                    this.queueInProgress.next(true);
                    this.queueInProgress = null;
                    // finished the queue
                }
                done();
            }
        }
    }
}
